import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { usePlans } from '../../contexts/PlanContext';
import { useCategories } from '../../contexts/CategoryContext';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { generateBudget } from '../../services/onboardingService';
import TypewriterText from '../../components/TypewriterText';

const OnboardingStep13 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { responses, setBudgetGenerated, markStepCompleted } = useOnboarding();
  const { refreshPlans, plans, currentMonthPlan } = usePlans();
  const { categories, refreshCategories } = useCategories();
  const { preferences } = useUserPreferences();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [generationStarted, setGenerationStarted] = useState(false);
  const progress = (13 / 14) * 100;
  const hasGeneratedRef = useRef(false);
  const currentMonthPlanRef = useRef(currentMonthPlan);
  const categoriesRef = useRef(categories);

  // Keep refs updated
  useEffect(() => {
    currentMonthPlanRef.current = currentMonthPlan;
  }, [currentMonthPlan]);

  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  // First effect: Generate budget and refresh plans
  useEffect(() => {
    // Prevent duplicate generation
    if (hasGeneratedRef.current || generationStarted) {
      return;
    }

    const generatePlan = async () => {
      try {
        // Mark as started immediately to prevent duplicate calls
        hasGeneratedRef.current = true;
        setGenerationStarted(true);

        // Check if plan and categories already exist - skip if found
        if (plans.length > 0 && categories.length > 0) {
          console.log('Plan and categories already exist, skipping generation');
          setBudgetGenerated(true);
          setIsLoading(false);
          return;
        }

        console.log('Starting budget generation...');

        // Call backend to generate budget structure with language preference
        await generateBudget(responses, preferences.language);

        // Refresh plans - this will trigger PlanContext to automatically:
        // 1. Load plans
        // 2. Set currentPlan
        // 3. Load currentMonthPlan (via useEffect)
        await refreshPlans();

        // Wait for currentMonthPlan to be available (set by PlanContext's useEffect)
        let waitCount = 0;
        while (!currentMonthPlanRef.current?.id && waitCount < 40) {
          await new Promise(resolve => setTimeout(resolve, 250));
          waitCount++;
        }

        console.log('Current month plan ready:', currentMonthPlanRef.current?.id);

        // Force refresh categories - don't rely on CategoryContext's useEffect
        // because it might not trigger if currentMonthPlan was already set
        if (currentMonthPlanRef.current?.id) {
          await refreshCategories();
          console.log('Categories refresh called');

          // Wait for categories to actually load
          let catWaitCount = 0;
          while (categoriesRef.current.length === 0 && catWaitCount < 40) {
            await new Promise(resolve => setTimeout(resolve, 250));
            catWaitCount++;
          }

          console.log('Categories loaded:', categoriesRef.current.length);
        }

        console.log('Budget generation complete');

        // Mark as complete and transition to display
        setBudgetGenerated(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Error generating budget:', err);
        setError(err instanceof Error ? err.message : t('onboarding.step13.error'));
        setIsLoading(false);
        // Reset on error so user can retry
        hasGeneratedRef.current = false;
        setGenerationStarted(false);
      }
    };

    generatePlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = () => {
    markStepCompleted(13);
    navigate('/onboarding/step-14');
  };

  return (
    <OnboardingLayout progress={progress} showBackButton={false}>
      {isLoading ? (
        // Loading State
        <div className="bg-white border-4 border-[#077f5d] p-8" style={{ boxShadow: '6px 6px 0 #077f5d' }}>
          <div className="text-center">
            <div className="flex items-center justify-center mb-8">
              <div
                className="bg-[#3cbe8c] border-4 border-[#077f5d] p-1s"
                style={{ boxShadow: '6px 6px 0 #077f5d' }}
              >
                <Loader2 className="w-12 h-12 text-[#077f5d] animate-spin" />
              </div>
            </div>
            <h2 className="text-base text-center text-[#077f5d] mb-4 uppercase" style={{ fontFamily: "'Press Start 2P', monospace" }}>
              {t('onboarding.step13.loading_title')}
            </h2>
            <div className="space-y-3 mb-4">
              <TypewriterText
                texts={[
                  '......'
                ]}
                speed={35}
                delayBetween={500}
                showCursor={true}
                className="text-[#1a1a1a] font-mono text-center"
                onFinish={() => console.log('Typing done')}
                startOnView={false}
              />
            </div>

            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Display State
        <>
          <div
            className="bg-white border-4 border-[#077f5d] shadow-pixel p-8 pb-28 max-h-[calc(100vh-8rem)] overflow-y-auto"
            style={{ boxShadow: '6px 6px 0 #077f5d' }}
          >
            <div className="text-center mb-6">
              <h2 className="text-base text-center text-[#077f5d] mb-4 uppercase" style={{ fontFamily: "'Press Start 2P', monospace" }}>
                {t('onboarding.step13.success_title')}
              </h2>
              <div className="space-y-3 mb-4">
                <TypewriterText
                  texts={[
                    t('onboarding.step13.subtitle')
                  ]}
                  speed={35}
                  delayBetween={500}
                  showCursor={true}
                  className="text-[#1a1a1a] font-mono text-center"
                  onFinish={() => console.log('Typing done')}
                  startOnView={false}
                />
              </div>

              {categories.length === 0 ? (
                <div className="text-[#1a1a1a] font-mono">{t('onboarding.step13.no_categories')}</div>
              ) : (
                <div className="space-y-6 mb-6">
                  {categories.map(group => (
                    <div
                      key={group.id}
                      className="bg-white border-2 border-[#077f5d]"
                      style={{
                        boxShadow: '4px 4px 0 #077f5d',
                        transition: 'none'
                      }}
                    >
                      {/* Group Header */}
                      <div className="p-4 bg-[#a9edba] border-b-2 border-[#077f5d]">
                        <div className="flex items-center justify-between mb-3">
                          <h3
                            className="font-bold text-[#077f5d] uppercase text-left"
                            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '10px', lineHeight: '1.4' }}
                          >
                            {group.name.split(' ').length === 2 ? (
                              <>
                                {group.name.split(' ')[0]}
                                <br />
                                {group.name.split(' ')[1]}
                              </>
                            ) : (
                              group.name
                            )}
                          </h3>
                          <span
                            className="text-[#077f5d] text-xs uppercase text-right"
                            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '8px' }}
                          >
                            <Trans i18nKey="onboarding.step13.allocation_budget" components={{ br: <br /> }} />
                          </span>
                        </div>
                        {/* Chunky pixel progress bar for group */}
                        <div className="h-3 bg-white border-2 border-[#077f5d]">
                          <div
                            className="h-full bg-[#3cbe8c]"
                            style={{ width: '0%', transition: 'none' }}
                          />
                        </div>
                      </div>

                      {/* Child Categories */}
                      {group.children && group.children.length > 0 && (
                        <div className="divide-y-2 divide-[#cdf7bd]">
                          {group.children.map(child => (
                            <div key={child.id} className="p-4 bg-white">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-[#1a1a1a] font-mono text-xs text-left flex-1">{child.name}</span>
                                <div
                                  className="inline-flex font-mono font-bold text-xs items-center bg-[#dffbd0] text-[#077f5d] px-3 py-1 border-2 border-[#077f5d] flex-shrink-0"
                                >
                                  {(() => {
                                    const symbols: Record<string, string> = {
                                      'IDR': 'Rp',
                                      'USD': '$',
                                      'EUR': '€',
                                      'GBP': '£',
                                      'JPY': '¥',
                                      'SGD': 'S$',
                                      'MYR': 'RM',
                                      'INR': '₹'
                                    };
                                    return symbols[preferences.currency] || preferences.currency;
                                  })()}
                                </div>
                              </div>
                              {/* Chunky pixel progress bar for child */}
                              <div className="h-3 bg-[#cdf7bd] border-2 border-[#077f5d]">
                                <div
                                  className="h-full bg-[#3cbe8c]"
                                  style={{ width: '0%', transition: 'none' }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Fixed floating button */}
          <div className="fixed bottom-0 left-0 right-0 p-8 from-white via-white to-transparent pointer-events-none">
            <button
              type="button"
              onClick={handleNext}
              className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase pointer-events-auto"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '12px',
                boxShadow: '6px 6px 0 #077f5d',
                transition: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {t('onboarding.step13.next')}
            </button>
          </div>
        </>
      )}
    </OnboardingLayout>
  );
};

export default OnboardingStep13;
